<template>
  <div class="animated fadeIn orcamentos-page">
    <div class="orcamentos-header">
      <div class="orcamentos-header__left">
        <h1 class="orcamentos-title">Orçamentos</h1>
        <p class="orcamentos-subtitle">
          {{ rotuloTotalResultados }}
        </p>
      </div>
      <b-button
        size="sm"
        variant="primary"
        class="orcamentos-novo-btn"
        :disabled="!permissaoCarregada || disabled"
        @click="newBudget"
      >
        <i class="fa fa-plus"></i> Novo Orçamento
      </b-button>
    </div>

    <div class="orcamentos-summary">
      <div class="orcamentos-summary-card">
        <div class="orcamentos-summary-card__icon orcamentos-summary-card__icon--purple">
          <i class="fa fa-line-chart"></i>
        </div>
        <div class="orcamentos-summary-card__body">
          <span class="orcamentos-summary-card__label">TOTAL NO PERÍODO</span>
          <span class="orcamentos-summary-card__value orcamentos-summary-card__value--purple">{{
            totalPeriodo | money
          }}</span>
          <div class="orcamentos-summary-card__meta">
            <i class="fa fa-calendar"></i>
            <span>{{ mesLabel }}</span>
            <span v-if="isMesAtual" class="orcamentos-pill orcamentos-pill--atual"
              >Mês atual</span
            >
          </div>
        </div>
      </div>

      <div class="orcamentos-summary-card">
        <div class="orcamentos-summary-card__icon orcamentos-summary-card__icon--blue">
          <i class="fa fa-file-text-o"></i>
        </div>
        <div class="orcamentos-summary-card__body">
          <span class="orcamentos-summary-card__label">ORÇAMENTOS</span>
          <span class="orcamentos-summary-card__value orcamentos-summary-card__value--blue"
            >{{ totalResultados }} registros</span
          >
        </div>
      </div>

      <div class="orcamentos-summary-card">
        <div class="orcamentos-summary-card__icon orcamentos-summary-card__icon--blue">
          <i class="fa fa-file-text-o"></i>
        </div>
        <div class="orcamentos-summary-card__body">
          <span class="orcamentos-summary-card__label">EM ABERTO</span>
          <span class="orcamentos-summary-card__value orcamentos-summary-card__value--blue"
            >{{ totalEmAberto }} orçamentos</span
          >
        </div>
      </div>
    </div>

    <div class="orcamentos-filters">
      <div class="orcamentos-filters__row">
        <div class="orcamentos-search">
          <i class="fa fa-search orcamentos-search__icon"></i>
          <input
            v-model="buscaLocal"
            type="search"
            class="orcamentos-search__input"
            placeholder="Buscar por cliente, código ou CNPJ..."
            @keyup.enter="aoSubmeterBusca"
            @input="aoBuscarComDebounce"
          />
        </div>
        <div class="orcamentos-month">
          <button
            type="button"
            class="orcamentos-month__nav"
            @click="shiftMonth(-1)"
          >
            <i class="fa fa-chevron-left"></i>
          </button>
          <div class="orcamentos-month__display">
            <i class="fa fa-calendar"></i>
            <span>{{ mesLabel }}</span>
            <span v-if="isMesAtual" class="orcamentos-pill orcamentos-pill--atual"
              >Atual</span
            >
          </div>
          <button
            type="button"
            class="orcamentos-month__nav"
            :disabled="isMesAtual"
            @click="shiftMonth(1)"
          >
            <i class="fa fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <div class="orcamentos-filters__row orcamentos-filters__row--pills">
        <div class="orcamentos-pills">
          <span class="orcamentos-pills__label">FILIAL</span>
          <button
            v-for="filial in filiais"
            :key="filial.cd_filial"
            type="button"
            class="orcamentos-chip"
            :class="{
              'orcamentos-chip--active': filialSelecionada === Number(filial.cd_filial),
            }"
            @click="toggleFilial(Number(filial.cd_filial))"
          >
            {{ labelFilial(filial) }}
          </button>
        </div>
        <div class="orcamentos-pills orcamentos-pills--right">
          <span class="orcamentos-pills__label">STATUS</span>
          <button
            type="button"
            class="orcamentos-chip"
            :class="{ 'orcamentos-chip--active': statusFilter === 'todos' }"
            @click="setStatusFilter('todos')"
          >
            Todos
          </button>
          <button
            type="button"
            class="orcamentos-chip"
            :class="{ 'orcamentos-chip--active': statusFilter === 'aberto' }"
            @click="setStatusFilter('aberto')"
          >
            Aberto
          </button>
          <button
            type="button"
            class="orcamentos-chip"
            :class="{
              'orcamentos-chip--active': statusFilter === 'cancelado',
            }"
            @click="setStatusFilter('cancelado')"
          >
            Cancelado
          </button>
        </div>
      </div>
    </div>

    <div class="orcamentos-table-wrap">
      <div v-if="carregando" class="orcamentos-loading" aria-busy="true">
        <b-spinner small label="Carregando..."></b-spinner>
        <span>Carregando orçamentos...</span>
      </div>
      <my-data-table
        v-else
        id="budgetsTable"
        :search="buscaLocal"
        :fields="resultadoBusca.fields"
        :sortColumn="resultadoBusca.sortColumn"
        :sortDirection="resultadoBusca.sortDirection"
        :totalResults="resultadoBusca.totalResults"
        :results="resultadoBusca.results"
        :resultsPerPage="resultadoBusca.resultsPerPage"
        :totalPages="resultadoBusca.totalPages"
        :page="resultadoBusca.page"
        :pages="resultadoBusca.pages"
        :buttons="'end'"
        :showResultsPerPage="false"
        :showSearch="false"
        class="font-12"
        v-on:doSearch="carregarOrcamentos"
      >
        <template v-slot:default="value">{{
          value.propertyValue | format(value.propertyName)
        }}</template>

        <template v-slot:id="value">
          <span class="orcamentos-codigo">#{{ value.propertyValue }}</span>
        </template>

        <template v-slot:status="value">
          <span
            class="orcamentos-status"
            :class="statusClass(value.propertyValue)"
          >
            <span class="orcamentos-status__dot"></span>
            {{ statusLabel(value.propertyValue) }}
          </span>
        </template>

        <template v-slot:cd_filial="value">
          <span :class="badgeFilial(value.propertyValue)">{{
            getFilial(value.propertyValue)
          }}</span>
        </template>

        <template v-slot:razao_social="value">
          <span class="orcamentos-razao">{{
            value.propertyValue | toUpper
          }}</span>
        </template>

        <template v-slot:usuario_criador="value">
          <span
            class="orcamentos-usuario-criador"
            :title="value.propertyValue || ''"
            >{{ value.propertyValue || "—" }}</span
          >
        </template>

        <template v-slot:total="value">
          <div class="orcamentos-total-cell moeda-real">
            {{ value.propertyValue | money }}
            <orcamento-alerta
              v-if="
                (value.row.status == 'D' || value.row.status == 'S') &&
                value.propertyValue > 0
              "
              :orcamentoId="value.row.id"
            ></orcamento-alerta>
          </div>
        </template>

        <template #buttons="value">
          <div class="btn-acoes">
            <b-button
              class="my-data-table-row-button orcamentos-action orcamentos-action--edit"
              size="sm"
              v-b-tooltip.hover.bottom.noninteractive
              title="Abrir orçamento"
              aria-label="Abrir orçamento"
              @click="openBudget(value.row)"
            >
              <i class="fa fa-edit"></i>
            </b-button>

            <b-button
              v-if="!disabled"
              class="my-data-table-row-button orcamentos-action orcamentos-action--dup"
              size="sm"
              v-b-tooltip.hover.bottom.noninteractive
              title="Duplicar"
              @click="confirmDuplicate(value.row)"
            >
              <i class="fa fa-copy"></i>
            </b-button>

            <b-button
              v-if="canDelete(value.row) && !disabled"
              class="my-data-table-row-button orcamentos-action orcamentos-action--del"
              size="sm"
              v-b-tooltip.hover.bottom.noninteractive
              title="Excluir"
              @click="confirmDelete(value.row)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </div>
        </template>
        <template #buttonsCaption>Ações</template>
      </my-data-table>
    </div>
  </div>
</template>

<style scoped>
.orcamentos-page {
  padding: 0.5rem 0 1.5rem;
}

.orcamentos-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.85rem;
  margin-bottom: 1.25rem;
}

.orcamentos-summary-card {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 12px;
  padding: 0.95rem 1.15rem;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
  min-width: 0;
}

.orcamentos-summary-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.orcamentos-summary-card__icon--purple {
  background: #f0edff;
  color: #6b5ce7;
}

.orcamentos-summary-card__icon--blue {
  background: #e8f1ff;
  color: #2563eb;
}

.orcamentos-summary-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.orcamentos-summary-card__label {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #8a8f98;
}

.orcamentos-summary-card__value {
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.25;
}

.orcamentos-summary-card__value--purple {
  color: #6b5ce7;
}

.orcamentos-summary-card__value--blue {
  color: #1e3a5f;
}

.orcamentos-summary-card__meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: #6b7280;
  flex-wrap: wrap;
}

.orcamentos-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.orcamentos-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.2;
}

.orcamentos-subtitle {
  margin: 0.25rem 0 0;
  color: #1E3A5F;
  font-size: 0.9rem;
}

.orcamentos-novo-btn {
  white-space: nowrap;
  align-self: center;
  font-weight: 600;
  padding: 0.45rem 0.95rem !important;
  border-radius: 8px !important;
  background-color: #7C3AED !important;
  border-color: #7C3AED !important;
}

.orcamentos-novo-btn:hover:not(:disabled),
.orcamentos-novo-btn:focus:not(:disabled) {
  background-color: #5804CA !important;
  border-color: #5804CA !important;
}

.orcamentos-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
}

.orcamentos-pill--atual {
  background: #e8f8ef;
  color: #1a9b56;
}

.orcamentos-filters {
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 12px;
  padding: 1rem 1.15rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
}

.orcamentos-filters__row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.orcamentos-filters__row--pills {
  margin-top: 0.85rem;
  justify-content: space-between;
}

.orcamentos-search {
  position: relative;
  flex: 1 1 280px;
  min-width: 200px;
}

.orcamentos-search__icon {
  position: absolute;
  left: 0.9rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9aa0a8;
}

.orcamentos-search__input {
  width: 100%;
  height: 42px;
  border: 1px solid #e0e3ea;
  border-radius: 10px;
  padding: 0 1rem 0 2.4rem;
  font-size: 0.9rem;
  color: #1a1a1a;
  outline: none;
  background: #fafbfc;
}

.orcamentos-search__input:focus {
  border-color: #6b5ce7;
  background: #fff;
}

.orcamentos-month {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
}

.orcamentos-month__nav {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e0e3ea;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.orcamentos-month__nav:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.orcamentos-month__display {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid #e0e3ea;
  border-radius: 10px;
  background: #fff;
  font-weight: 600;
  font-size: 0.9rem;
  color: #1a1a1a;
  min-width: 160px;
  justify-content: center;
}

.orcamentos-pills {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.orcamentos-pills__label {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #8a8f98;
  margin-right: 0.25rem;
}

.orcamentos-chip {
  border: 1px solid #d8dce6;
  background: #fff;
  color: #4b5563;
  border-radius: 999px;
  padding: 0.3rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  line-height: 1.2;
}

.orcamentos-chip--active {
  background: #1f2937;
  border-color: #1f2937;
  color: #fff;
}

.orcamentos-table-wrap {
  position: relative;
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 14px;
  padding: 0.35rem 0.5rem 0.85rem;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
  overflow: hidden;
}

.orcamentos-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 280px;
  color: #6b7280;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.orcamentos-loading .spinner-border {
  width: 1.25rem;
  height: 1.25rem;
  border-width: 0.15em;
  color: #6b5ce7;
}

.orcamentos-table-wrap ::v-deep .row {
  margin-left: 0;
  margin-right: 0;
}

.orcamentos-table-wrap ::v-deep .col-md-12 {
  padding-left: 0;
  padding-right: 0;
}

.orcamentos-table-wrap ::v-deep table {
  margin: 0 !important;
  border: none !important;
  background: transparent;
}

.orcamentos-table-wrap ::v-deep table.table-bordered,
.orcamentos-table-wrap ::v-deep table.table-bordered th,
.orcamentos-table-wrap ::v-deep table.table-bordered td {
  border: none !important;
}

.orcamentos-table-wrap ::v-deep thead th {
  background: #f8fafc !important;
  border: none !important;
  border-bottom: 1px solid #eef0f4 !important;
  color: #9aa3b2 !important;
  font-size: 0.68rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em;
  text-align: left !important;
  text-transform: uppercase;
  padding: 0.55rem 0.75rem !important;
  vertical-align: middle !important;
  white-space: nowrap;
}

.orcamentos-table-wrap ::v-deep thead th#button {
  text-align: center !important;
}

.orcamentos-table-wrap ::v-deep thead th[aria-sort],
.orcamentos-table-wrap ::v-deep .table.b-table > thead > tr > [aria-sort] {
  background-image: none !important;
  cursor: pointer;
}

.orcamentos-table-wrap ::v-deep tbody td {
  border: none !important;
  border-bottom: 1px solid #e5e8ef !important;
  background: #fff !important;
  color: #374151;
  font-size: 0.82rem;
  padding: 0.4rem 0.75rem !important;
  vertical-align: middle !important;
}

.orcamentos-table-wrap ::v-deep tbody tr:nth-child(even) td {
  background: #f4f6fb !important;
}

.orcamentos-table-wrap ::v-deep tbody tr:last-child td {
  border-bottom: none !important;
}

.orcamentos-table-wrap ::v-deep tbody tr:hover td {
  background: #eef1f8 !important;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination {
  margin: 0.55rem 0.35rem 0;
  padding: 0.7rem 0.85rem;
  gap: 0.55rem;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__info {
  color: #8a93a5;
  font-size: 0.78rem;
  font-weight: 500;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav {
  margin: 0;
  flex-wrap: wrap;
  gap: 0.15rem;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item {
  margin: 0;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:first-child {
  margin-right: auto;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:last-child {
  margin-left: auto;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-link {
  min-width: 2rem;
  height: 2.15rem;
  padding: 0 0.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: #374151;
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1;
  box-shadow: none;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:not(.disabled) .page-link:hover {
  background: #f3f4f6;
  color: #111827;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item.active .page-link {
  background: #eef0f4;
  color: #111827;
  font-weight: 700;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item.disabled .page-link {
  color: #c0c6d2;
  background: transparent;
  opacity: 1;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:first-child .page-link,
.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:last-child .page-link {
  min-width: auto;
  height: 2.25rem;
  padding: 0 0.85rem;
  border: 1px solid #d8dde8;
  border-radius: 10px;
  background: #fff;
  color: #374151;
  font-size: 0.82rem;
  font-weight: 600;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:first-child:not(.disabled) .page-link:hover,
.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:last-child:not(.disabled) .page-link:hover {
  background: #f8fafc;
  border-color: #c5ccd9;
  color: #111827;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:first-child.disabled .page-link,
.orcamentos-table-wrap ::v-deep .my-data-table-pagination__nav .page-item:last-child.disabled .page-link {
  background: #f8fafc;
  border-color: #e5e8ef;
  color: #c0c6d2;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__edge {
  gap: 0.45rem;
}

.orcamentos-table-wrap ::v-deep .my-data-table-pagination__edge .fa {
  font-size: 0.72rem;
}

.btn-acoes {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 0.4rem;
}

.font-12 {
  font-size: 12px;
}

.orcamentos-codigo {
  color: #6b5ce7;
  font-weight: 700;
  font-size: 0.9rem;
}

.orcamentos-table-wrap ::v-deep tbody td.column-codigo {
  text-align: left !important;
}

.orcamentos-razao {
  font-weight: 600;
  color: #1f2937;
}

.orcamentos-usuario-criador {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  line-height: 1.3;
}

.orcamentos-total-cell {
  color: #1d4ed8;
  font-weight: 700;
  white-space: nowrap;
  min-width: 5rem;
  text-align: right;
}

.orcamentos-total-cell.moeda-real::before {
  float: left;
  content: "R$";
  padding-right: 0.35rem;
}

.orcamentos-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 999px;
  border: 1px solid transparent;
  padding: 0.28rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}

.orcamentos-status__dot {
  width: 0.4rem;
  height: 0.4rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.orcamentos-status--aberto {
  background: #e8f4ff;
  color: #2563eb;
  border-color: #2563eb;
}

.orcamentos-status--aberto .orcamentos-status__dot {
  background: #2563eb;
}

.orcamentos-status--cancelado {
  background: #ffe8ec;
  color: #e11d48;
  border-color: #e11d48;
}

.orcamentos-status--cancelado .orcamentos-status__dot {
  background: #e11d48;
}

.orcamentos-status--finalizado {
  background: #e8f8ef;
  color: #059669;
  border-color: #059669;
}

.orcamentos-status--finalizado .orcamentos-status__dot {
  background: #059669;
}

.badge-filial {
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.6rem;
  border-radius: 999px;
  padding: 0.28rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  background-color: #6b7280;
}

.orcamentos-table-wrap ::v-deep tbody td:has(.badge-filial) {
  text-align: left !important;
}

.badge-filial--1 {
  background-color: #0075eb;
}

.badge-filial--2 {
  background-color: #ff6d13;
}

.orcamentos-action {
  flex-shrink: 0;
  border: 1px solid transparent !important;
  border-radius: 50% !important;
  width: 1.85rem !important;
  height: 1.85rem !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  box-shadow: none !important;
}

.orcamentos-action--view {
  background: #eee9ff !important;
  border-color: #cfc4f5 !important;
  color: #6b5ce7 !important;
}

.orcamentos-action--edit {
  background: #e8f8ef !important;
  border-color: #b8e0c8 !important;
  color: #059669 !important;
}

.orcamentos-action--dup {
  background: #e8f1ff !important;
  border-color: #b8d0f7 !important;
  color: #2563eb !important;
}

.orcamentos-action--del {
  background: #ffe8ec !important;
  border-color: #f5b8c4 !important;
  color: #e11d48 !important;
}

.orcamentos-table-wrap ::v-deep #cd_filialbudgetsTable {
  width: 7% !important;
}

.orcamentos-table-wrap ::v-deep #idbudgetsTable {
  width: 6% !important;
}

.orcamentos-table-wrap ::v-deep #razao_socialbudgetsTable {
  width: 18% !important;
}

.orcamentos-table-wrap ::v-deep #cnpjbudgetsTable {
  width: 11% !important;
}

.orcamentos-table-wrap ::v-deep #data_emissaobudgetsTable,
.orcamentos-table-wrap ::v-deep #data_validadebudgetsTable {
  width: 9% !important;
  text-align: center !important;
}

.orcamentos-table-wrap ::v-deep tbody td.text-center {
  text-align: center !important;
}

.orcamentos-table-wrap ::v-deep #statusbudgetsTable {
  width: 7% !important;
}

.orcamentos-table-wrap ::v-deep #usuario_criadorbudgetsTable {
  width: 12% !important;
}

.orcamentos-table-wrap ::v-deep #totalbudgetsTable {
  width: 5% !important;
}

.orcamentos-table-wrap ::v-deep #button {
  width: 1% !important;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .orcamentos-summary {
    grid-template-columns: 1fr;
  }

  .orcamentos-filters__row--pills {
    flex-direction: column;
    align-items: flex-start;
  }

  .orcamentos-pills--right {
    width: 100%;
  }
}
</style>

<style>
.orcamento-confirm-modal {
  border: none !important;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
}

.orcamento-confirm-modal .modal-header.orcamento-confirm-modal__header {
  display: flex;
  align-items: center;
  background: #0075eb;
  border-bottom: none;
  padding: 0.9rem 1.1rem;
  color: #fff;
}

.orcamento-confirm-modal .modal-header .modal-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
}

.orcamento-confirm-modal .modal-header .close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 1.15rem;
  font-weight: 400;
  line-height: 1;
  text-shadow: none;
  opacity: 1;
}

.orcamento-confirm-modal .modal-header .close:hover,
.orcamento-confirm-modal .modal-header .close:focus {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  opacity: 1;
  outline: none;
}

.orcamento-confirm-modal .modal-body.orcamento-confirm-modal__body {
  padding: 1.15rem 1.1rem;
  color: #23282c;
  font-size: 0.92rem;
  line-height: 1.45;
}

.orcamento-confirm-modal .modal-footer.orcamento-confirm-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.55rem;
  border-top: 1px solid #edf0f2;
  padding: 0.85rem 1.1rem 1rem;
  background: #fff;
}

.btn-orcamento-confirm-ok {
  min-width: 72px;
  border-radius: 8px !important;
  background: #0075eb !important;
  border: 1px solid #0075eb !important;
  color: #fff !important;
  font-weight: 600;
  padding: 0.4rem 1rem;
}

.btn-orcamento-confirm-ok:hover,
.btn-orcamento-confirm-ok:focus {
  background: #005bb7 !important;
  border-color: #005bb7 !important;
  color: #fff !important;
}

.btn-orcamento-confirm-cancel {
  min-width: 72px;
  border-radius: 8px !important;
  background: #fff !important;
  border: 1px solid #d8dee6 !important;
  color: #6c757d !important;
  font-weight: 600;
  padding: 0.4rem 1rem;
}

.btn-orcamento-confirm-cancel:hover,
.btn-orcamento-confirm-cancel:focus {
  background: #f8f9fa !important;
  color: #495057 !important;
}
</style>

<script>
import Revenda from "../../../services/revenda";
import Filial from "../../../services/filial";
import { httpNoLoadingFunctions } from "../../../services/configNoLoading";
import Orcamento from "../../../services/orcamento";
import MyDataTable from "@/components/ui/MyDataTable";
import OrcamentoAlerta from "./OrcamentoAlerta";
import GrupoPermissao from "../../../services/grupoPermissao";
import {
  EventBus,
  ORCAMENTO_EDIT_FORBIDDEN_EVENT,
} from "../../../helpers/eventBus";
import Vue from "vue";

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const LEGENDAS_CAMPOS = {
  cd_filial: "Filial",
  id: "Cód.",
  razao_social: "Cliente",
  cnpj: "CNPJ",
  data_emissao: "Emissão",
  data_validade: "Validade",
  status: "Status",
  usuario_criador: "Criado por",
  total: "Total",
};

const RESULTADOS_POR_PAGINA = 25;

export default {
  name: "Orcamentos",
  components: { MyDataTable, OrcamentoAlerta },
  data() {
    const agora = new Date();
    return {
      primeiraCargaOrcamentos: true,
      resultadoBusca: {},
      disabled: true,
      permissaoCarregada: false,
      buscaLocal: "",
      debounceBusca: null,
      carregando: true,
      requisicaoOrcamentosId: 0,
      filiais: [],
      filialSelecionada: null,
      statusFilter: "todos",
      mes: agora.getMonth() + 1,
      ano: agora.getFullYear(),
      totalPeriodo: 0,
      totalEmAberto: 0,
      confirmacaoEmProcesso: false,
      params: {
        params: {
          resultsPerPage: RESULTADOS_POR_PAGINA,
          sortColumn: "id",
          sortDirection: "desc",
          page: 1,
          search: "",
        },
      },
    };
  },
  computed: {
    mesLabel() {
      return `${MESES[this.mes - 1]} ${this.ano}`;
    },
    isMesAtual() {
      const agora = new Date();
      return (
        this.mes === agora.getMonth() + 1 && this.ano === agora.getFullYear()
      );
    },
    rotuloTotalResultados() {
      const total = this.resultadoBusca.totalResults || 0;
      return `${total} registro(s) encontrado(s)`;
    },
    totalResultados() {
      return this.resultadoBusca.totalResults || 0;
    },
  },
  async beforeMount() {
    EventBus.$on(
      ORCAMENTO_EDIT_FORBIDDEN_EVENT,
      this.revogarPermissaoEdicao
    );
    const podeAcessar = await this.validaPermissao();
    if (!podeAcessar) return;

    this.loadFiliais();
    this.carregarOrcamentos(this.params);
  },
  beforeDestroy() {
    if (this.debounceBusca) clearTimeout(this.debounceBusca);
    EventBus.$off(
      ORCAMENTO_EDIT_FORBIDDEN_EVENT,
      this.revogarPermissaoEdicao
    );
  },
  methods: {
    revogarPermissaoEdicao() {
      this.disabled = true;
    },
    async validaPermissao() {
      try {
        const response = await GrupoPermissao.getPermissao("/vendas/orcamentos");
        const permissao = response.data.data;
        if (!permissao) {
          this.$router.push("/");
          return false;
        }

        this.disabled = permissao.edita != "1";
        this.permissaoCarregada = true;
        return true;
      } catch (error) {
        this.$helper.showErrorResponse(error);
        this.$router.push("/");
        return false;
      }
    },
    canDelete(data) {
      return data.status != "E" && data.status != "I";
    },
    montarQueryFiltros() {
      const parts = [
        `statusFilter=${encodeURIComponent(this.statusFilter)}`,
        `mes=${this.mes}`,
        `ano=${this.ano}`,
      ];
      if (this.filialSelecionada) {
        parts.push(`cd_filial=${this.filialSelecionada}`);
      }
      return parts.join("&");
    },
    normalizarParams(data) {
      const base = {
        resultsPerPage: RESULTADOS_POR_PAGINA,
        sortColumn: "id",
        sortDirection: "desc",
        page: 1,
        search: this.buscaLocal || "",
      };
      if (data && data.params) {
        return {
          params: {
            ...base,
            ...data.params,
            resultsPerPage: RESULTADOS_POR_PAGINA,
            search:
              data.params.search !== undefined
                ? data.params.search
                : this.buscaLocal || "",
          },
        };
      }
      return { params: base };
    },
    carregarOrcamentos(data = null) {
      if (!this.primeiraCargaOrcamentos)
        httpNoLoadingFunctions.cancelPendingRequests();

      const request = this.normalizarParams(data || this.params);
      this.params = request;
      this.buscaLocal = request.params.search || "";
      this.carregando = true;
      const requisicaoId = ++this.requisicaoOrcamentosId;

      Revenda.getOrcamentos(request, this.montarQueryFiltros())
        .then((response) => {
          if (requisicaoId !== this.requisicaoOrcamentosId) return;
          this.aplicarResultadoBusca(response.data.data || {});
          this.primeiraCargaOrcamentos = false;
        })
        .catch((e) => {
          if (requisicaoId !== this.requisicaoOrcamentosId) return;
          if (e && e.__CANCEL__) return;
          if (
            e.response &&
            e.response.status &&
            e.response.status === 404 &&
            e.response.data
          ) {
            this.aplicarResultadoBusca(e.response.data.data || {});
          } else {
            this.$helper.showErrorResponse(e);
          }
          this.primeiraCargaOrcamentos = false;
        })
        .finally(() => {
          if (requisicaoId === this.requisicaoOrcamentosId) {
            this.carregando = false;
          }
        });
    },
    aplicarResultadoBusca(data) {
      const result = data || {};
      if (Array.isArray(result.fields)) {
        result.fields = result.fields.map((field) => ({
          ...field,
          caption: LEGENDAS_CAMPOS[field.name] || field.caption,
        }));
      }
      this.resultadoBusca = result;
      this.totalPeriodo = result.totalPeriodo || 0;
      this.totalEmAberto = result.totalEmAberto || 0;
    },
    recarregarComFiltros() {
      this.params = {
        params: {
          ...this.params.params,
          page: 1,
          search: this.buscaLocal || "",
          resultsPerPage: RESULTADOS_POR_PAGINA,
        },
      };
      this.carregarOrcamentos(this.params);
    },
    aoSubmeterBusca() {
      if (this.debounceBusca) clearTimeout(this.debounceBusca);
      this.recarregarComFiltros();
    },
    aoBuscarComDebounce() {
      if (this.debounceBusca) clearTimeout(this.debounceBusca);
      this.debounceBusca = setTimeout(() => {
        this.recarregarComFiltros();
      }, 1000);
    },
    shiftMonth(delta) {
      let mes = this.mes + delta;
      let ano = this.ano;
      if (mes < 1) {
        mes = 12;
        ano -= 1;
      } else if (mes > 12) {
        mes = 1;
        ano += 1;
      }
      const agora = new Date();
      if (
        ano > agora.getFullYear() ||
        (ano === agora.getFullYear() && mes > agora.getMonth() + 1)
      ) {
        return;
      }
      this.mes = mes;
      this.ano = ano;
      this.recarregarComFiltros();
    },
    toggleFilial(cdFilial) {
      this.filialSelecionada =
        this.filialSelecionada === cdFilial ? null : cdFilial;
      this.recarregarComFiltros();
    },
    setStatusFilter(status) {
      this.statusFilter = status;
      this.recarregarComFiltros();
    },
    openBudget(row) {
      this.$router.push("orcamentos/orcamento/" + row.id);
    },
    newBudget() {
      if (!this.permissaoCarregada || this.disabled) return;
      this.$router.push("produtos");
    },
    loadFiliais() {
      Filial.getAtivas()
        .then((response) => {
          this.filiais = response.data.data || [];
        })
        .catch(() => {
          this.filiais = [];
        });
    },
    labelFilial(filial) {
      if (!filial) return "N/A";
      return filial.cidade || "N/A";
    },
    getFilial(cd_filial) {
      const filial = this.filiais.find(
        (item) => Number(item.cd_filial) === Number(cd_filial)
      );
      return filial ? this.labelFilial(filial) : cd_filial;
    },
    badgeFilial(cd_filial) {
      return `badge badge-filial badge-filial--${cd_filial}`;
    },
    statusLabel(status) {
      if (status === "C") return "Cancelado";
      if (status === "I") return "Finalizado";
      return "Aberto";
    },
    statusClass(status) {
      if (status === "C") return "orcamentos-status--cancelado";
      if (status === "I") return "orcamentos-status--finalizado";
      return "orcamentos-status--aberto";
    },
    getConfirmModalOptions(overrides = {}) {
      return {
        title: "Atenção",
        size: "sm",
        centered: true,
        hideHeaderClose: false,
        contentClass: "orcamento-confirm-modal",
        headerClass: "orcamento-confirm-modal__header",
        bodyClass: "orcamento-confirm-modal__body",
        footerClass: "orcamento-confirm-modal__footer",
        okVariant: "orcamento-confirm-ok",
        cancelVariant: "orcamento-confirm-cancel",
        okTitle: "Sim",
        cancelTitle: "Não",
        autoFocusButton: "ok",
        ...overrides,
      };
    },
    confirmDuplicate(row) {
      if (this.disabled || this.confirmacaoEmProcesso || this.$store.state.ui.loading) return;

      this.confirmacaoEmProcesso = true;
      this.$bvModal
        .msgBoxConfirm(
          "Tem certeza que deseja duplicar o Orçamento Nº." + row.id + "?",
          this.getConfirmModalOptions()
        )
        .then((value) => {
          if (!value) {
            this.confirmacaoEmProcesso = false;
            return;
          }

          this.$store.commit("ui/LOADING", true);
          Orcamento.getByCodigo(row.id)
            .then((response) => {
              const orcamento = response.data.data;
              return Orcamento.duplicarOrcamento(orcamento);
            })
            .then((response) => {
              this.$store.commit("ui/LOADING", false);
              this.$helper.showMsg(response.data.msg, response.data.type);
              this.carregarOrcamentos(this.params);
            })
            .catch((e) => {
              this.$store.commit("ui/LOADING", false);
              this.$helper.showErrorResponse(e);
            })
            .finally(() => {
              this.confirmacaoEmProcesso = false;
            });
        })
        .catch((err) => {
          this.confirmacaoEmProcesso = false;
          this.$helper.showErrorResponse(err);
        });
    },
    confirmDelete(row) {
      if (this.disabled || this.confirmacaoEmProcesso || this.$store.state.ui.loading) return;

      this.confirmacaoEmProcesso = true;
      this.$bvModal
        .msgBoxConfirm(
          "Tem certeza que deseja excluir o Orçamento Nº." + row.id + "?",
          this.getConfirmModalOptions()
        )
        .then((value) => {
          if (!value) {
            this.confirmacaoEmProcesso = false;
            return;
          }

          this.$store.commit("ui/LOADING", true);
          Orcamento.deleteOrcamento(row.id)
            .then((response) => {
              this.$store.commit("ui/LOADING", false);
              this.$helper.showMsg(response.data.msg, response.data.type);
              this.carregarOrcamentos(this.params);
            })
            .catch((e) => {
              this.$store.commit("ui/LOADING", false);
              this.$helper.showErrorResponse(e);
            })
            .finally(() => {
              this.confirmacaoEmProcesso = false;
            });
        })
        .catch((err) => {
          this.confirmacaoEmProcesso = false;
          this.$helper.showErrorResponse(err);
        });
    },
  },
  filters: {
    format(value, field) {
      switch (field) {
        case "razao_social":
          return Vue.filter("toUpper")(value);
        case "data_emissao":
          return Vue.filter("formatDate")(value);
        case "data_validade":
          return Vue.filter("formatDate")(value);
        default:
          return value;
      }
    },
  },
};
</script>
